'use client'
import { useState } from 'react'
import { useCartStore } from '@/stores/cartStore'
import dynamic from 'next/dynamic'

const FiCheck = dynamic(() => import('react-icons/fi').then(mod => mod.FiCheck), { ssr: false })
const FiPlus = dynamic(() => import('react-icons/fi').then(mod => mod.FiPlus), { ssr: false })

interface QuickAddButtonProps {
  productId: string; // Product ID to add
  quantity?: number; // Optional quantity, defaults to 1
}

export default function QuickAddButton({ productId, quantity = 1 }: QuickAddButtonProps) {
  const [isAdding, setIsAdding] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean>(false)
  const addItem = useCartStore((state) => state.addItem)
  
  const handleClick = async () => {
    setIsAdding(true)
    try {
      // Add the product to the cart using the Zustand store with the specified quantity
      addItem(productId, quantity, undefined)
      
      // Show success animation
      setSuccess(true)
      setTimeout(() => {
        setSuccess(false)
        setIsAdding(false)
      }, 1000) // Slightly longer animation for better visibility
    } catch (err) {
      console.error('Error adding product to cart:', err)
      setIsAdding(false)
    }
  }
  
  return (
    <button
      onClick={handleClick}
      disabled={isAdding}
      aria-label="Add to Cart"
      className={`
        w-[42px] sm:w-[50px] aspect-square flex items-center justify-center rounded-full transition-all duration-300
        backdrop-blur-sm shadow-sm
        ${success 
          ? 'bg-status-success text-static-white scale-110' 
          : 'bg-text-main text-bg-main hover:scale-105 hover:bg-secondary'
        }
      `}
    >
      <span className={`text-xl transition-transform duration-300 ${success ? 'animate-scale-check' : ''}`}>
        {success ? <FiCheck /> : <FiPlus />}
      </span>
    </button>
  )
}
